import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import GlassCard from "components/GlassCard";
import axiosInstance from "services/httpInterceptor";

// Truncate a company name to its first word: "Medtronic Neurovascular" → "Medtronic"
const firstWord = (name) => (name ? name.split(" ")[0] : name);

// Format a large number: 4200 → "4.2k"
const fmt = (n) => {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

// Fetch the v2 engagement stats for a single company
const fetchStats = (platform, companyId, presetKey) =>
  axiosInstance
    .get(`/v1/frontend/${platform}/v2/general/stats`, {
      params: { companyId, preset: presetKey.toUpperCase() },
    })
    .then((r) => r.data)
    .catch(() => null);

/**
 * StatCard
 *
 * One card in the comparative strip. Shows the same metric for both companies.
 * label    — card heading (e.g. "Total Posts")
 * rows     — [{ name, value, color }]
 * loading  — show skeleton
 */
function StatCard({ label, rows, loading }) {
  return (
    <GlassCard sx={{ height: "100%", "&:hover": { transform: "none", boxShadow: "none" } }}>
      <VuiTypography
        variant="caption"
        sx={{
          color: "rgba(255,255,255,0.9)",
          fontSize: "0.68rem",
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          display: "block",
          mb: 1,
        }}
      >
        {label}
      </VuiTypography>

      {loading ? (
        <>
          <Skeleton variant="text" width="70%" height={18} sx={{ bgcolor: "rgba(255,255,255,0.07)", mb: 0.5 }} />
          <Skeleton variant="text" width="55%" height={18} sx={{ bgcolor: "rgba(255,255,255,0.07)" }} />
        </>
      ) : (
        rows.map((row, i) => (
          <VuiBox key={i} display="flex" alignItems="baseline" gap={0.75} mb={i < rows.length - 1 ? 0.5 : 0}>
            <VuiBox
              sx={{ width: 6, height: 6, borderRadius: "50%", background: row.color, flexShrink: 0, mt: "1px" }}
            />
            <VuiTypography
              variant="caption"
              sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", flexShrink: 0, minWidth: 60 }}
            >
              {firstWord(row.name)}:
            </VuiTypography>
            <VuiTypography
              variant="caption"
              sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,1)", fontWeight: "regular" }}
            >
              {row.value}
            </VuiTypography>
          </VuiBox>
        ))
      )}
    </GlassCard>
  );
}

/**
 * ComparativeStatsStrip
 *
 * Four stat cards — each showing BOTH companies side by side.
 * Metrics: Total Posts, Total Engagements, Avg Engagements/Post, Top Content Category.
 *
 * Props:
 *   platform       — "linkedin" | "x"
 *   competitorId   — MongoDB ObjectId of the competitor company
 *   clientId       — MongoDB ObjectId of the client company
 *   presetKey      — period preset (e.g. "LAST_QUARTER")
 *   competitorName — display name for competitor
 *   clientName     — display name for client
 */
function ComparativeStatsStrip({ platform, competitorId, clientId, presetKey, competitorName, clientName }) {
  const [competitorStats, setCompetitorStats] = useState(null);
  const [clientStats, setClientStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!competitorId || !clientId || !presetKey) return;
    setLoading(true);
    Promise.all([
      fetchStats(platform, competitorId, presetKey),
      fetchStats(platform, clientId, presetKey),
    ]).then(([cmp, cli]) => {
      setCompetitorStats(cmp);
      setClientStats(cli);
      setLoading(false);
    });
  }, [platform, competitorId, clientId, presetKey]);

  const competitorColor = "#2CD9FF"; // matches barChartOptions fill index 0
  const clientColor = "#0075FF";     // matches barChartOptions fill index 1

  const cards = [
    {
      label: "Total Posts",
      rows: [
        { name: competitorName, value: competitorStats?.totalPosts ?? "—", color: competitorColor },
        { name: clientName, value: clientStats?.totalPosts ?? "—", color: clientColor },
      ],
    },
    {
      label: "Total Engagements",
      rows: [
        { name: competitorName, value: fmt(competitorStats?.totalEngagements), color: competitorColor },
        { name: clientName, value: fmt(clientStats?.totalEngagements), color: clientColor },
      ],
    },
    {
      label: "Avg Engagements / Post",
      rows: [
        { name: competitorName, value: competitorStats ? Math.round(competitorStats.avgEngagementsPerPost ?? 0) : "—", color: competitorColor },
        { name: clientName, value: clientStats ? Math.round(clientStats.avgEngagementsPerPost ?? 0) : "—", color: clientColor },
      ],
    },
    {
      label: "Top Content Category",
      rows: [
        { name: competitorName, value: competitorStats?.mostCommonContentCategory ?? "—", color: competitorColor },
        { name: clientName, value: clientStats?.mostCommonContentCategory ?? "—", color: clientColor },
      ],
    },
  ];

  return (
    <Grid container spacing={2}>
      {cards.map((card) => (
        <Grid key={card.label} item xs={12} sm={6} lg={3}>
          <StatCard label={card.label} rows={card.rows} loading={loading} />
        </Grid>
      ))}
    </Grid>
  );
}

ComparativeStatsStrip.propTypes = {
  platform: PropTypes.string.isRequired,
  competitorId: PropTypes.string,
  clientId: PropTypes.string,
  presetKey: PropTypes.string,
  competitorName: PropTypes.string,
  clientName: PropTypes.string,
};

ComparativeStatsStrip.defaultProps = {
  competitorId: null,
  clientId: null,
  presetKey: null,
  competitorName: "Competitor",
  clientName: "Your Company",
};

export default ComparativeStatsStrip;
